// Wykres przepływu na punkcie (/chart/:alias/:markerId i /chart/:alias/finish):
// liczba wejść w przedziałach czasu (15/30/60 min) z podziałem na kategorie.
// Czas zawsze jako chwila (ms epoki) — przedziały wyrównane do pełnych kwadransów czasu polskiego
// (przesunięcie Warszawy to pełne godziny, więc wystarczy zaokrąglenie ms do wielokrotności przedziału).
import { PALETTE } from '../config/palette.js'
import { isSupportRole } from './runner-role.js'
import { FINISH_TYPES, markerType, passTimesForMarker } from './checkpoint-results.js'
import { parseApiTime } from './polish-time.js'

export const BUCKET_OPTIONS = [15, 30, 60]
export const FINISH_KEY = 'finish'
const NO_CATEGORY = '__none__'

// Kolory zapasowe dla kategorii bez koloru z panelu ORG — z palety, w stałej kolejności.
const FALLBACK_COLORS = [
  PALETTE.primary,
  PALETTE.genderK,
  PALETTE.success,
  PALETTE.warning,
  PALETTE.panel,
  PALETTE.bibBadge,
  PALETTE.liveStrong,
  PALETTE.primaryDeep,
  PALETTE.warningInk,
  PALETTE.panelHover
]

const labelFormat = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  hour: '2-digit',
  minute: '2-digit'
})
const dayFormat = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: 'numeric',
  month: 'numeric'
})

export function formatHM (ms) {
  return labelFormat.format(new Date(ms))
}

export function formatDay (ms) {
  return dayFormat.format(new Date(ms))
}

// Meta: oficjalny czas mety (finish_time), a bez niego pierwsze przejście przez marker mety (GPS).
export function finishPassTimes (runners, markers, splits) {
  const finishMarkers = (markers || []).filter(m => FINISH_TYPES.includes(markerType(m)))
  const fromSplits = new Map()
  for (const marker of finishMarkers) {
    for (const [runnerId, ms] of passTimesForMarker(splits, marker.id)) {
      const prev = fromSplits.get(runnerId)
      if (prev == null || ms < prev) fromSplits.set(runnerId, ms)
    }
  }
  const passes = new Map()
  for (const runner of runners || []) {
    const key = String(runner.id)
    const ms = parseApiTime(runner.finish_time) ?? fromSplits.get(key)
    if (ms != null) passes.set(key, ms)
  }
  return passes
}

// Kategorie w kolejności z API; kolor z kategorii, a bez niego kolejny zapasowy.
export function categoryLegend (categories, runners) {
  const list = []
  // Zapasowe kolory pomijają te, które organizator już nadał innym kategoriom.
  const taken = new Set((categories || []).map(c => String(c.color || '').toLowerCase()).filter(Boolean))
  const spare = FALLBACK_COLORS.filter(color => !taken.has(color.toLowerCase()))
  const pool = spare.length ? spare : FALLBACK_COLORS
  let fallback = 0
  for (const c of categories || []) {
    const color = c.color || pool[fallback++ % pool.length]
    list.push({ id: String(c.id), name: c.name || `Kategoria ${c.id}`, color })
  }
  const known = new Set(list.map(c => c.id))
  const hasOrphans = (runners || []).some(r => r.category_id == null || !known.has(String(r.category_id)))
  if (hasOrphans) list.push({ id: NO_CATEGORY, name: 'Bez kategorii', color: PALETTE.textMuted })
  return list
}

// Domyślny przedział: krótkie zawody 15 min, dłuższe 30, wielodniowe 60.
export function suggestBucketMinutes (passTimes) {
  let min = null
  let max = null
  for (const ms of passTimes.values()) {
    if (min == null || ms < min) min = ms
    if (max == null || ms > max) max = ms
  }
  if (min == null) return 15
  const hours = (max - min) / 3600000
  if (hours <= 6) return 15
  if (hours <= 16) return 30
  return 60
}

// Przedziały od pierwszego wejścia do ostatniego (albo do „teraz”, gdy podane — wtedy widać przestoje).
// Zwraca { buckets: [{ startMs, endMs, counts: {catId: n}, total }], categories, total, peak }.
export function buildArrivalBuckets ({ runners, passTimes, categories, bucketMinutes, untilMs = null }) {
  const legend = categoryLegend(categories, runners)
  const known = new Set(legend.map(c => c.id))
  const sizeMs = bucketMinutes * 60000
  const arrivals = []
  for (const runner of runners || []) {
    if (isSupportRole(runner)) continue
    const ms = passTimes.get(String(runner.id))
    if (ms == null) continue
    const cat = runner.category_id != null && known.has(String(runner.category_id))
      ? String(runner.category_id)
      : NO_CATEGORY
    arrivals.push({ ms, cat })
  }
  if (arrivals.length === 0) return { buckets: [], categories: legend.filter(c => c.id !== NO_CATEGORY), total: 0, peak: null }

  let first = Infinity
  let last = -Infinity
  for (const a of arrivals) {
    if (a.ms < first) first = a.ms
    if (a.ms > last) last = a.ms
  }
  if (untilMs != null && untilMs > last) last = untilMs
  const firstBucket = Math.floor(first / sizeMs) * sizeMs
  const count = Math.floor((last - firstBucket) / sizeMs) + 1

  const buckets = []
  for (let i = 0; i < count; i++) {
    const startMs = firstBucket + i * sizeMs
    buckets.push({ startMs, endMs: startMs + sizeMs, counts: {}, total: 0 })
  }
  const used = new Set()
  for (const a of arrivals) {
    const bucket = buckets[Math.floor((a.ms - firstBucket) / sizeMs)]
    bucket.counts[a.cat] = (bucket.counts[a.cat] || 0) + 1
    bucket.total++
    used.add(a.cat)
  }

  let peak = null
  for (const b of buckets) if (!peak || b.total > peak.total) peak = b

  return {
    buckets,
    // Legenda tylko z kategoriami, które ktoś reprezentuje na punkcie — kolory się nie zmieniają,
    // bo są przypisane do kategorii, nie do pozycji na liście.
    categories: legend.filter(c => used.has(c.id)),
    total: arrivals.length,
    peak
  }
}
